// src/utils/appUpdate.js
// 3.62.30 (owner): an open portal picks up a new deployment by itself.
// Each deploy writes /portal/version.json (scripts/writeVersion.js). The
// portal reads it every 5 minutes, when the window comes back into view and
// when the connection returns. When it names a different version, the portal
// reloads at a moment that can't interrupt anyone:
//   - the window is in the background, or nobody has touched it for 5 minutes
//     (3.63.2, owner: was 10; moving the mouse over it counts as use too),
//     and nothing is typed, open, sending or uploading;
//   - or the person moves to another page (leavePage, from goToTab), from
//     Compose too (3.64.2, owner: its form isn't kept when you leave it).
// Never on Compose, with a pop-up open, while something is being saved or
// uploaded, or with typing not yet saved on the page (3.63.2: a search box,
// like Find a fax, isn't unsaved work and doesn't count). If no safe moment
// comes within 10 minutes, a small bar offers Refresh (UpdateBar).
// One automatic reload per version per browser tab: if the new version still
// isn't there afterwards (a stale copy somewhere), only the bar is shown.
import { CURRENT_VERSION } from '../components/WhatsNew';

const URL_      = `${process.env.PUBLIC_URL}/version.json`;
const EVERY_MS  = 5 * 60 * 1000;
const IDLE_MS   = 5 * 60 * 1000;    // 3.63.2 (owner): was 10
const BAR_MS    = 10 * 60 * 1000;
const TRIED_KEY = 'faxPortal.updateTried';

let newer = null;          // the deployed version, when it differs from this copy
let foundAt = 0;
let writes = 0;            // saves and uploads in progress (anything but GET/HEAD)
let holds = 0;             // holdUpdates(): e.g. an announcement sending
let typed = false;         // typing on this page since the last page change
let touchedAt = Date.now();
let tab = '';
let lastCheck = 0;
const listeners = new Set();

const tell = () => listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } });
const readTried = () => { try { return window.sessionStorage.getItem(TRIED_KEY); } catch { return null; } };
const markTried = v => { try { window.sessionStorage.setItem(TRIED_KEY, v); } catch { /* ignore */ } };

export function updateReady() { return newer; }
export function showUpdateBar() { return !!newer && (Date.now() - foundAt >= BAR_MS || readTried() === newer); }
export function onUpdateChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function setUpdateTab(t) { tab = t || ''; }
// Something that mustn't be cut off by a reload; returns the release
export function holdUpdates() {
  holds++;
  let done = false;
  return () => { if (!done) { done = true; holds--; } };
}

// The safe moment: nothing that a reload would lose
function quiet() {
  if (!newer || readTried() === newer) return false;
  if (writes > 0 || holds > 0 || typed || tab === 'compose') return false;
  if (popUpOpen()) return false;
  return true;
}

// A pop-up on screen (some, like the menu under your name, stay in the page
// hidden while closed)
function popUpOpen() {
  return [...document.querySelectorAll('[role="dialog"], [aria-modal="true"], [role="alertdialog"]')].some(el =>
    !el.closest('[aria-hidden="true"]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
}

function reload(to) {
  markTried(newer);
  if (to) window.location.assign(to); else window.location.reload();
}

function tryNow() {
  if (!quiet()) return;
  if (document.hidden || Date.now() - touchedAt >= IDLE_MS) reload();
}

// goToTab: moving to another page is a natural moment (what was typed on the
// old page goes either way). Not while saving. 3.64.2 (owner): leaving
// Compose counts too: its form is cleared on leaving anyway, and a fax still
// sending is a save in progress
export function leavePage(path) {
  typed = false;
  if (!newer || readTried() === newer || writes > 0 || holds > 0) return false;
  reload(path);
  return true;
}

// The bar's Refresh: the person chose it
export function refreshNow() { markTried(newer || CURRENT_VERSION); window.location.reload(); }

async function check() {
  if (Date.now() - lastCheck < 60 * 1000) return;
  lastCheck = Date.now();
  try {
    const res = await fetch(`${URL_}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const v = String((await res.json()).version || '');
    if (!/^\d+\.\d+\.\d+$/.test(v) || v === CURRENT_VERSION) return;
    if (newer !== v) {
      newer = v; foundAt = Date.now();
      navigator.serviceWorker?.getRegistration?.().then(r => r && r.update()).catch(() => {});
      tell();
    }
    tryNow();
  } catch { /* offline or no file (npm start): try again later */ }
}

let started = false;
export function startUpdateChecks() {
  if (started || typeof window === 'undefined') return;
  started = true;

  // Count saves and uploads in progress, by fetch and by XMLHttpRequest
  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD') return origFetch(input, init);
    writes++;
    return origFetch(input, init).finally(() => { writes--; });
  };
  const X = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (X) {
    const open = X.open, send = X.send;
    X.open = function (method, ...rest) { this.__cpWrite = !/^(GET|HEAD)$/i.test(String(method)); return open.call(this, method, ...rest); };
    X.send = function (...args) {
      if (this.__cpWrite) { writes++; this.addEventListener('loadend', () => { writes--; }, { once: true }); }
      return send.apply(this, args);
    };
  }

  // Typing (or picking a file) on a page: wait for the next page change.
  // 3.63.2: not in a search box (type search, a combobox, or a box that says
  // Find…/Search…): what's typed there isn't work to lose
  const isField = el => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
  const isSearch = el => el.type === 'search' || el.getAttribute('role') === 'combobox'
    || /^(find|search)\b/i.test(el.getAttribute('placeholder') || el.getAttribute('aria-label') || '');
  const onType = e => { if (isField(e.target) && !isSearch(e.target)) typed = true; };
  document.addEventListener('input', onType, true);
  document.addEventListener('change', onType, true);
  document.addEventListener('drop', () => { typed = true; }, true);
  ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach(ev =>
    window.addEventListener(ev, () => { touchedAt = Date.now(); }, { capture: true, passive: true }));
  // 3.63.2 (owner): moving the mouse over the portal counts as use too
  window.addEventListener('pointermove', () => { touchedAt = Date.now(); }, { passive: true });

  document.addEventListener('visibilitychange', () => { if (document.hidden) tryNow(); else check(); });
  window.addEventListener('focus', check);
  window.addEventListener('online', check);
  setInterval(() => { check(); tryNow(); }, EVERY_MS);
  setInterval(tryNow, 60 * 1000);
  setTimeout(check, 30 * 1000);
}
